import { z } from 'zod';
export const MAX_DELAY_MS = 300_000;
export const MAX_CHUNKS = 64;
export const MAX_CHUNK_DELAY_MS = 60_000;
export const DEFAULT_CONTENT_TYPE = 'text/html; charset=utf-8';
export const PAYLOAD_PARAM_OPTIONS = [
    { value: 'html', label: 'HTML', paramLabel: '?html=', contentType: 'text/html', language: 'html' },
    { value: 'script', label: 'Inline Script', paramLabel: '?script=', contentType: 'text/html', language: 'javascript' },
    { value: 'js', label: 'JavaScript', paramLabel: '?js=', contentType: 'application/javascript', language: 'javascript' },
    { value: 'json', label: 'JSON', paramLabel: '?json=', contentType: 'application/json', language: 'json' },
    { value: 'text', label: 'Plain Text', paramLabel: '?text=', contentType: 'text/plain', language: 'plaintext' },
    { value: 'body', label: 'Raw Body', paramLabel: '?body=', contentType: 'text/html', language: 'html' },
];
export const VALID_PAYLOAD_PARAMS = new Set(PAYLOAD_PARAM_OPTIONS.map((o) => o.value));
export const LANGUAGE_OPTIONS = [
    { value: 'html', label: 'HTML' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'json', label: 'JSON' },
    { value: 'plaintext', label: 'Plain text' },
];
export const VALID_LANGUAGES = new Set(LANGUAGE_OPTIONS.map((o) => o.value));
// The editor language that fits a body param. The editor derives it
// automatically; the field is still persisted so a share round-trips.
export function languageForParam(param) {
    const opt = PAYLOAD_PARAM_OPTIONS.find((o) => o.value === param);
    return opt ? opt.language : 'html';
}
export const headerRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    value: z.string(),
});
export const editorStateSchema = z.object({
    status: z.number().int().min(200).max(599),
    delay: z.number().int().min(0).max(MAX_DELAY_MS),
    payloadParam: z.enum(['html', 'script', 'js', 'json', 'text', 'body']),
    editorLanguage: z.enum(['html', 'javascript', 'json', 'plaintext']),
    editorContent: z.string(),
    headers: z.array(headerRowSchema),
    // Timed drip: split the body into `chunks` pieces and wait `chunkDelay` ms
    // between them. Both optional with defaults so pre-existing shares parse.
    chunks: z.number().int().min(1).max(MAX_CHUNKS).default(1),
    chunkDelay: z.number().int().min(0).max(MAX_CHUNK_DELAY_MS).default(0),
});
export function pickStatusCode(input) {
    const candidate = firstDefined(input?.status, input?.statuscode, input?.code);
    const value = typeof candidate === 'string' ? parseInt(candidate, 10) : Number(candidate);
    if (Number.isInteger(value) && value >= 200 && value <= 599)
        return value;
    return 200;
}
export function parseDelayMs(input) {
    if (input === undefined || input === null || input === '')
        return 0;
    const ms = Number(input);
    if (!Number.isFinite(ms) || ms <= 0)
        return 0;
    return Math.round(Math.min(ms, MAX_DELAY_MS));
}
export function parseChunks(input) {
    if (input === undefined || input === null || input === '')
        return 1;
    const n = Number(input);
    if (!Number.isFinite(n) || n < 1)
        return 1;
    return Math.round(Math.min(n, MAX_CHUNKS));
}
export function parseChunkDelayMs(input) {
    if (input === undefined || input === null || input === '')
        return 0;
    const ms = Number(input);
    if (!Number.isFinite(ms) || ms <= 0)
        return 0;
    return Math.round(Math.min(ms, MAX_CHUNK_DELAY_MS));
}
export function firstDefined(...values) {
    for (const v of values) {
        if (v !== undefined && v !== null && v !== '')
            return v;
    }
    return undefined;
}
export const SHARE_NAME_MAX_LEN = 80;
// A readable default name for a share: the <title> if the body is HTML,
// otherwise the first non-empty line with tags stripped. Falls back to a
// one-line summary of the status so an empty body still gets a name.
export function deriveShareName(state) {
    const content = state.editorContent ?? '';
    const title = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(content)?.[1]?.trim();
    const firstLine = content
        .split(/\r?\n/)
        .map((l) => l.replace(/<[^>]*>/g, '').trim())
        .find((l) => l.length > 0);
    const base = (title && title.length > 0 ? title : firstLine) ?? '';
    const cleaned = base.replace(/\s+/g, ' ').trim();
    if (cleaned.length > 0)
        return cleaned.length > 48 ? `${cleaned.slice(0, 47)}…` : cleaned;
    return `${state.status} ${state.payloadParam}`;
}
